<script setup lang="ts">
import type { ApexOptions } from 'apexcharts'
import { onMounted, ref } from 'vue'
import { useTheme } from 'vuetify'
import { hexToRgb } from '@core/utils/colorConverter'
import {
  type DashboardMetrics,
  type DistributionChartData,
  type TimeSeriesChartData,
  dashboardApi,
} from '@/api/entities/review/dashboard'

definePage({
  meta: {
    title: '리글 대시보드',
    action: 'read',
    subject: 'Dashboard',
  },
})

const { handleError } = useErrorHandler()
const vuetifyTheme = useTheme()

// 날짜 필터
const startDate = ref(
  new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
)

const endDate = ref(new Date().toISOString().split('T')[0])

// 상태
const metricsLoading = ref(false)
const chartsLoading = ref(false)

const metrics = ref<DashboardMetrics | null>(null)
const collectionChart = ref<TimeSeriesChartData | null>(null)
const successRateChart = ref<TimeSeriesChartData | null>(null)
const clientChart = ref<DistributionChartData | null>(null)
const driverChart = ref<DistributionChartData | null>(null)

// 색상 팔레트
const CHART_COLORS = [
  '#0EA5E9',
  '#F59E0B',
  '#10B981',
  '#6366F1',
  '#EC4899',
  '#8B5CF6',
  '#0891B2',
  '#16A34A',
  '#D97706',
  '#DC2626',
]

// 메트릭 변화율 계산
function getChangeRate(current: number, previous: number): number {
  if (previous === 0)
    return 0
  
  return Math.round(((current - previous) / previous) * 100)
}

function getChangeColor(current: number, previous: number): string {
  if (current > previous)
    return 'success'
  if (current < previous)
    return 'error'
  
  return 'secondary'
}

// Bar 차트 옵션 (수집량 추이)
function getBarChartOptions(data: TimeSeriesChartData): ApexOptions {
  const colors = `rgba(${hexToRgb(vuetifyTheme.current.value.colors['on-surface'])},0.87)`

  return {
    chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false } },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
    xaxis: {
      categories: data.labels,
      labels: { style: { colors } },
    },
    yaxis: { labels: { style: { colors } } },
    colors: ['#3B82F6'],
    dataLabels: { enabled: false },
    tooltip: { theme: vuetifyTheme.name.value },
    grid: { borderColor: `rgba(${hexToRgb(vuetifyTheme.current.value.colors['on-surface'])},0.12)` },
  }
}

// Line 차트 옵션 (성공률)
function getLineChartOptions(data: TimeSeriesChartData): ApexOptions {
  const colors = `rgba(${hexToRgb(vuetifyTheme.current.value.colors['on-surface'])},0.87)`

  return {
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: {
      categories: data.labels,
      labels: { style: { colors } },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        style: { colors },
        formatter: (v: number) => `${v.toFixed(0)}%`,
      },
    },
    colors: ['#10B981'],
    dataLabels: { enabled: false },
    tooltip: {
      theme: vuetifyTheme.name.value,
      y: { formatter: (v: number) => `${v.toFixed(2)}%` },
    },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 0.8, opacityFrom: 0.4, opacityTo: 0.1 },
    },
    grid: { borderColor: `rgba(${hexToRgb(vuetifyTheme.current.value.colors['on-surface'])},0.12)` },
  }
}

// Donut 차트 옵션 (분포)
function getDonutChartOptions(data: DistributionChartData): ApexOptions {
  const secondaryText = `rgba(${hexToRgb(vuetifyTheme.current.value.colors['on-surface'])},0.6)`

  return {
    chart: { type: 'donut' },
    labels: data.labels,
    colors: CHART_COLORS.slice(0, data.labels.length),
    legend: {
      position: 'bottom',
      fontSize: '13px',
      labels: { colors: secondaryText },
    },
    dataLabels: {
      formatter: (val: number) => `${Math.round(val)}%`,
    },
    stroke: { width: 0 },
    tooltip: { theme: vuetifyTheme.name.value },
  }
}

// 데이터 로드
async function loadMetrics() {
  metricsLoading.value = true
  try {
    const res = await dashboardApi.getMetrics()

    metrics.value = res.data
  }
  catch (e) {
    handleError(e)
  }
  finally {
    metricsLoading.value = false
  }
}

async function loadCharts() {
  chartsLoading.value = true

  const params = { start_date: startDate.value, end_date: endDate.value }

  try {
    const [collection, successRate, clientDist, driverDist] = await Promise.all([
      dashboardApi.getCollectionChart(params),
      dashboardApi.getSuccessRateChart(params),
      dashboardApi.getClientDistributionChart(params),
      dashboardApi.getDriverDistributionChart(params),
    ])

    collectionChart.value = collection.data
    successRateChart.value = successRate.data
    clientChart.value = clientDist.data
    driverChart.value = driverDist.data
  }
  catch (e) {
    handleError(e)
  }
  finally {
    chartsLoading.value = false
  }
}

async function refresh() {
  await Promise.all([loadMetrics(), loadCharts()])
}

onMounted(refresh)
</script>

<template>
  <div>
    <!-- 날짜 필터 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="4"
      >
        <AppDateTimePicker
          v-model="startDate"
          label="시작일"
          :config="{ dateFormat: 'Y-m-d' }"
        />
      </VCol>
      <VCol
        cols="12"
        md="4"
      >
        <AppDateTimePicker
          v-model="endDate"
          label="종료일"
          :config="{ dateFormat: 'Y-m-d' }"
        />
      </VCol>
      <VCol
        cols="12"
        md="4"
        class="d-flex align-center"
      >
        <VBtn
          color="primary"
          :loading="chartsLoading"
          @click="loadCharts"
        >
          조회
        </VBtn>
      </VCol>
    </VRow>

    <!-- 메트릭 카드 -->
    <VRow class="mb-4">
      <VCol
        v-if="metricsLoading"
        cols="12"
      >
        <VProgressLinear indeterminate />
      </VCol>
      <template v-else-if="metrics">
        <!-- 오늘 수집량 -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText>
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-body-2 text-medium-emphasis">오늘 수집량</span>
                <VIcon
                  icon="tabler-download"
                  size="24"
                  color="primary"
                />
              </div>
              <div class="text-h5 font-weight-bold mb-1">
                {{ metrics.today.count.toLocaleString() }}
              </div>
              <VChip
                :color="getChangeColor(metrics.today.count, metrics.today.previous)"
                size="small"
                variant="tonal"
              >
                {{ getChangeRate(metrics.today.count, metrics.today.previous) > 0 ? '+' : '' }}{{ getChangeRate(metrics.today.count, metrics.today.previous) }}% vs 전일
              </VChip>
            </VCardText>
          </VCard>
        </VCol>

        <!-- 주간 수집량 -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText>
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-body-2 text-medium-emphasis">주간 수집량</span>
                <VIcon
                  icon="tabler-calendar-week"
                  size="24"
                  color="info"
                />
              </div>
              <div class="text-h5 font-weight-bold mb-1">
                {{ metrics.weekly.count.toLocaleString() }}
              </div>
              <VChip
                :color="getChangeColor(metrics.weekly.count, metrics.weekly.previous)"
                size="small"
                variant="tonal"
              >
                {{ getChangeRate(metrics.weekly.count, metrics.weekly.previous) > 0 ? '+' : '' }}{{ getChangeRate(metrics.weekly.count, metrics.weekly.previous) }}% vs 전주
              </VChip>
            </VCardText>
          </VCard>
        </VCol>

        <!-- 월간 수집량 -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText>
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-body-2 text-medium-emphasis">월간 수집량</span>
                <VIcon
                  icon="tabler-calendar-month"
                  size="24"
                  color="success"
                />
              </div>
              <div class="text-h5 font-weight-bold mb-1">
                {{ metrics.monthly.count.toLocaleString() }}
              </div>
              <VChip
                :color="getChangeColor(metrics.monthly.count, metrics.monthly.previous)"
                size="small"
                variant="tonal"
              >
                {{ getChangeRate(metrics.monthly.count, metrics.monthly.previous) > 0 ? '+' : '' }}{{ getChangeRate(metrics.monthly.count, metrics.monthly.previous) }}% vs 전월
              </VChip>
            </VCardText>
          </VCard>
        </VCol>

        <!-- 활성 클라이언트 -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText>
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-body-2 text-medium-emphasis">활성 클라이언트</span>
                <VIcon
                  icon="tabler-users"
                  size="24"
                  color="warning"
                />
              </div>
              <div class="text-h5 font-weight-bold">
                {{ metrics.active_clients.toLocaleString() }}
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </template>
    </VRow>

    <!-- 차트 행 1: 수집량 추이 + 성공률 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="8"
      >
        <VCard>
          <VCardTitle class="pa-4">
            일별 이관 리뷰 수
          </VCardTitle>
          <VDivider />
          <VCardText>
            <div
              v-if="chartsLoading"
              class="d-flex justify-center align-center"
              style="height: 300px;"
            >
              <VProgressCircular indeterminate />
            </div>
            <VueApexCharts
              v-else-if="collectionChart && collectionChart.series[0]?.data.length"
              type="bar"
              height="300"
              :options="getBarChartOptions(collectionChart)"
              :series="collectionChart.series"
            />
            <div
              v-else
              class="text-center text-medium-emphasis py-8"
            >
              데이터 없음
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="4"
      >
        <VCard>
          <VCardTitle class="pa-4">
            이관 성공률 (%)
          </VCardTitle>
          <VDivider />
          <VCardText>
            <div
              v-if="chartsLoading"
              class="d-flex justify-center align-center"
              style="height: 300px;"
            >
              <VProgressCircular indeterminate />
            </div>
            <VueApexCharts
              v-else-if="successRateChart && successRateChart.series[0]?.data.length"
              type="line"
              height="300"
              :options="getLineChartOptions(successRateChart)"
              :series="successRateChart.series"
            />
            <div
              v-else
              class="text-center text-medium-emphasis py-8"
            >
              데이터 없음
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 차트 행 2: 클라이언트별 + 드라이버별 분포 -->
    <VRow>
      <VCol
        cols="12"
        md="6"
      >
        <VCard>
          <VCardTitle class="pa-4">
            클라이언트별 분포 (상위 10)
          </VCardTitle>
          <VDivider />
          <VCardText>
            <div
              v-if="chartsLoading"
              class="d-flex justify-center align-center"
              style="height: 300px;"
            >
              <VProgressCircular indeterminate />
            </div>
            <VueApexCharts
              v-else-if="clientChart && clientChart.series.length"
              type="donut"
              height="300"
              :options="getDonutChartOptions(clientChart)"
              :series="clientChart.series"
            />
            <div
              v-else
              class="text-center text-medium-emphasis py-8"
            >
              데이터 없음
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="6"
      >
        <VCard>
          <VCardTitle class="pa-4">
            드라이버별 분포 (상위 10)
          </VCardTitle>
          <VDivider />
          <VCardText>
            <div
              v-if="chartsLoading"
              class="d-flex justify-center align-center"
              style="height: 300px;"
            >
              <VProgressCircular indeterminate />
            </div>
            <VueApexCharts
              v-else-if="driverChart && driverChart.series.length"
              type="donut"
              height="300"
              :options="getDonutChartOptions(driverChart)"
              :series="driverChart.series"
            />
            <div
              v-else
              class="text-center text-medium-emphasis py-8"
            >
              데이터 없음
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
